import {
  Box,
  Grid,
  Typography,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import {t} from 'i18next';
import {Option} from '../../common/aur-select/AurSelect';
import AurServerSingleSelect from '../../common/aur-select-dropdown/AurServerSingleSelect';
import {useEffect, useRef, useState} from 'react';
import {getPermissionGroupList} from '../../../api/ClientManagementService';
import AurButton from '../../common/aur-button/AurButton';
import AurCheckbox from '../../common/aur-checkbox/AurCheckbox';
import AurClientSelect from '../../common/aur-select-dropdown/AurClientSingleSelect';
import {RootState} from '../../../redux/store';
import {useSelector, useDispatch} from 'react-redux';
import {setPermissionGroups} from '../../../redux/slices/permissionGroupSlice';
import {store} from '../../../redux/store';
import {
  mergePermissionGroupsWithWriteModel,
  mergePermissionGroupOptionLists,
  permissionGroupToItemType,
} from '../../../utils/permissionGroupItemUtils';
import {PermissionGroup} from '../../../types/permission-group/permissionGroup';
import {PERMISSION_GROUP_CATEGORY} from '../../../constants/constants';
import AurPermissionGroupSelect from '../../common/aur-select/AurPermissionGroupSelect';
import {getFilteredRoleOptionList} from '../../../utils/userManagementUtils';
import {AurClientProjectSectionHeader} from '../../common/aur-page-header/AurClientProjectHeader';
import {ItemType} from '../../pages/user-management/tabs/AddClientInfo';
import AurProjectSelect from '../../common/aur-select/AurProjectSelect';
import {ClientProject} from '../../../types/project-type/projectDetailsType';

type FieldError = {
  hasError: boolean;
  errorText: string;
};
type ClientInfoFormError = {
  client: FieldError;
  role: FieldError;
  permissionGroup: FieldError;
};

type ClientInfoFormProps = {
  roleOptions?: Option[];
  selectedClientId?: string;
  client?: string;
  selectedRoleId?: string;
  role?: string;
  selectedPermissionGroupId?: string;
  clientHCProjects?: ClientProject[];
  selectedHCProjectId?: string;
  formLabel?: string;
  containerClass?: string;
  isEditDisabled?: boolean;
  isValid?: boolean;
  isAdminRoleSelected?: boolean;
  clientFieldHidden?: boolean;
  projectFieldHidden?: boolean;
  buttonSectionHidden?: boolean;
  showHCProjects?: boolean;
  hasHCProjects?: boolean;
  hideWorkspaceDropdown?: boolean;
  error?: ClientInfoFormError;
  isCreateMode?: boolean;
  onClientChange?: (
    clientId: string,
    clientName: string,
    budgetConsumptionEnabled: boolean
  ) => void;
  onRoleChange?: (roleId: string) => void;
  onPermissionGroupChange?: (permissionGroupId: string) => void;
  onHCProjectChange?: (projectId: string) => void;
  onHCProjectCheckedChange: (value: boolean) => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onCreateNewPermissionGroup: () => void;
  /** When provided, parent-owned list (e.g. after CQRS create) takes precedence over local fetch */
  permissionGroupOptionList?: ItemType[];
};

// main Component
const ClientInfoForm = (props: ClientInfoFormProps) => {
  const {
    roleOptions = [],
    selectedClientId,
    client,
    selectedRoleId,
    role,
    selectedPermissionGroupId,
    clientHCProjects = [],
    selectedHCProjectId,
    formLabel = '',
    containerClass = '',
    isEditDisabled = false,
    isValid = false,
    isAdminRoleSelected = false,
    clientFieldHidden = false,
    projectFieldHidden = false,
    buttonSectionHidden = true,
    showHCProjects = false,
    hasHCProjects = false,
    isCreateMode = false,
    error,
    onClientChange,
    onRoleChange,
    onPermissionGroupChange,
    onHCProjectChange,
    onHCProjectCheckedChange,
    onSubmit,
    onCancel,
    onCreateNewPermissionGroup,
    permissionGroupOptionList,
  } = props;

  const lastClientRef = useRef({
    id: '',
    name: '',
  });

  if (selectedClientId) {
    lastClientRef.current.id = selectedClientId;
  }

  if (client) {
    lastClientRef.current.name = client;
  }

  const [permissionGroupOptions, setPermissionGroupOptions] = useState<
    ItemType[] | null
  >(null);

  const refreshPermissionGroups = useSelector(
    (state: RootState) => state.permissionGroups.refreshPermissionGroups
  );

  const selectedUserRoleForClient = useSelector(
    (state: RootState) => state?.userRole?.userRoleForSelectedClient
  );
  const usesParentPermissionGroupList = permissionGroupOptionList !== undefined;

  useEffect(() => {
    if (usesParentPermissionGroupList) {
      return;
    }
    if (refreshPermissionGroups && selectedClientId) {
      getPermissionForPermissionGroupSelect(selectedClientId);
    }
  }, [
    refreshPermissionGroups,
    usesParentPermissionGroupList,
    selectedClientId,
  ]);

  const getHCCheckbox = () => {
    return hasHCProjects ? (
      <div className="left-align-center-y" style={{marginTop: '1em'}}>
        <FormControlLabel
          control={<AurCheckbox />}
          checked={showHCProjects}
          className="hc-project-checkbox"
          onChange={(event: any) => {
            onHCProjectCheckedChange &&
              onHCProjectCheckedChange(event.target.checked);
          }}
          label={
            <Typography className="mist-color-500">
              {t('addEditTeamMember.form.highlyConfidentialProjectNote')}
            </Typography>
          }
          disabled={projectFieldHidden}
        />
      </div>
    ) : (
      <></>
    );
  };

  const dispatch = useDispatch();
  // get and set permission group options
  const getPermissionForPermissionGroupSelect = async (clientId: string) => {
    try {
      const result = await getPermissionGroupList(
        clientId,
        PERMISSION_GROUP_CATEGORY.CLIENT
      );
      const apiGroups = result.data.data.permissionGroups as PermissionGroup[];

      if (!apiGroups) return [];

      const permissionGroupsState = store.getState().permissionGroups;
      const mergedGroups = mergePermissionGroupsWithWriteModel(
        apiGroups,
        permissionGroupsState,
        clientId,
        PERMISSION_GROUP_CATEGORY.CLIENT
      );
      const permissionGroupOptionListFromApi: ItemType[] = mergedGroups.map(
        (group) => permissionGroupToItemType(group)
      );
      dispatch(setPermissionGroups({permissionGroups: mergedGroups}));
      setPermissionGroupOptions((prev) =>
        mergePermissionGroupOptionLists(
          permissionGroupOptionListFromApi,
          prev ?? []
        )
      );
    } catch (error) {
      console.error('getPermissionForPermissionGroupSelect:', error);
    }
  };

  const effectivePermissionGroupOptions =
    permissionGroupOptionList ?? permissionGroupOptions ?? [];

  //commenting this code as this is unused
  // const updatePageForClientError = () => {
  //   clientFieldState = {
  //     error: {
  //       message: t('userHomePage.clientLoadError'),
  //     },
  //   };
  // };

  // set error state
  let clientFieldState = {};
  let roleFieldState = {};
  let permissionGroupFieldState = {};
  if (error) {
    if (error.client?.hasError) {
      clientFieldState = {
        error: {
          message: error.client.errorText,
        },
      };
    }
    if (error.role?.hasError) {
      roleFieldState = {
        error: {
          message: error.role.errorText,
        },
      };
    }
    if (error.permissionGroup?.hasError) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      permissionGroupFieldState = {
        error: {
          message: error.permissionGroup.errorText,
        },
      };
    }
  }

  useEffect(() => {
    if (usesParentPermissionGroupList) {
      return;
    }
    if (selectedClientId) {
      getPermissionForPermissionGroupSelect(selectedClientId);
    } else {
      setPermissionGroupOptions(null);
    }
  }, [selectedClientId, usesParentPermissionGroupList]);

  return (
    <Grid
      container
      className={`add-client-info-container ${containerClass}`}
      spacing={3}
      width="1200px"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        width: '1500px !improtant',
      }}
    >
      {!clientFieldHidden && (
        <AurClientProjectSectionHeader
          titleTestId="aur-test-client-info-title"
          title={formLabel ? formLabel : t('addEditTeamMember.clientAccess')}
          subheader={t('addEditTeamMember.clientAccessSubheader')}
          style={{paddingTop: '0'}}
        />
      )}
      {!clientFieldHidden && (
        <Grid item sm={12} md={12} className="form-content">
          <AurClientSelect
            field={{
              value: selectedClientId || lastClientRef.current.id,
              label: client || lastClientRef.current.name,
              onChange: (option: Option) => {
                onClientChange?.(
                  option.value,
                  option.label,
                  option?.budgetConsumptionEnabled ?? false
                );
              },
            }}
            fieldState={clientFieldState}
            assignNewClient={true}
          />
        </Grid>
      )}

      <Grid item sm={12} md={12}>
        <Grid
          container
          spacing={3}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
          }}
        >
          <Grid item sm={12} md={12} className="form-content">
            <AurServerSingleSelect
              disabled={isEditDisabled}
              dataTestId="aur-test-role-dropdown"
              label={t('addEditTeamMember.form.role')}
              optionList={getFilteredRoleOptionList(
                selectedUserRoleForClient ?? '',
                roleOptions
              )}
              field={{
                value: selectedRoleId ?? '',
                label: role,
                onChange: (option) => {
                  onRoleChange?.(option.value);
                },
              }}
              fieldState={roleFieldState}
            />
          </Grid>
          {!isAdminRoleSelected && (
            <Grid item sm={8} md={12} className="form-content">
              <AurPermissionGroupSelect
                disabled={!(selectedClientId && selectedRoleId)}
                items={effectivePermissionGroupOptions}
                value={selectedPermissionGroupId || ''}
                onChange={(value) => {
                  onPermissionGroupChange && onPermissionGroupChange(value);
                }}
                label={t('addEditTeamMember.form.clientPermissionGroup')}
                clientId={selectedClientId}
                onCreateNewPermissionGroup={onCreateNewPermissionGroup}
                disabledMessage={t(
                  'addEditTeamMember.form.clientPermissionGroupDisabledMessage'
                )}
                subheaderLabel={t('common.clientPermGroupSubheader')}
                category={PERMISSION_GROUP_CATEGORY.CLIENT}
              />
            </Grid>
          )}
          {isAdminRoleSelected && (
            <Grid
              item
              sm={12}
              md={12}
              className="form-content"
              sx={{paddingTop: '10px'}}
            >
              <Typography className="client-info-hidden-note-text">
                {' '}
                {t('addEditTeamMember.form.sysAdminContentAccess')}
                <span>
                  {t('addEditTeamMember.form.projectAndWorkspaces')}
                </span>{' '}
                {t('addEditTeamMember.form.withClientinAurora')}
              </Typography>
              {getHCCheckbox()}
            </Grid>
          )}
          {isAdminRoleSelected &&
            showHCProjects &&
            !projectFieldHidden &&
            !isCreateMode && (
              <Grid container className="add-client-info-container" spacing={3}>
                <AurClientProjectSectionHeader
                  title={t('addEditTeamMember.projectAccess')}
                  subheader={t('addEditTeamMember.projectAccessSubheader')}
                  className="project-access-header-edit"
                />
                <Grid item sm={8} md={12} className="form-content">
                  <AurProjectSelect
                    field={{
                      value: selectedHCProjectId,
                      onChange: (event: SelectChangeEvent<string>) => {
                        onHCProjectChange &&
                          onHCProjectChange(event.target.value);
                      },
                    }}
                    fieldState={{}}
                    showHCProjects={showHCProjects}
                    isAdmin={isAdminRoleSelected}
                    disabled={false}
                    clientProjects={[]}
                    clientHCProjects={clientHCProjects}
                    unsavedProjects={[]}
                    projectItemsVisible={2}
                  />
                </Grid>
              </Grid>
            )}
        </Grid>
      </Grid>
      {!buttonSectionHidden && (
        <Grid
          item
          sm={12}
          md={12}
          className="text-right user-form"
          sx={{display: 'flex', justifyContent: 'flex-end'}}
        >
          <Box sx={{display: 'flex', flexDirection: 'row', gap: '16px'}}>
            <AurButton
              type="exit"
              label={t('buttons.dontSaveUpdate')}
              dataTestId="cancel-changes"
              size="Medium"
              disabled={isEditDisabled}
              onClick={() => {
                onCancel && onCancel();
              }}
            />

            <AurButton
              type="continue"
              label={t('common.saveChanges')}
              dataTestId="save-changes"
              size="Medium"
              disabled={isEditDisabled || !isValid}
              onClick={() => {
                onSubmit && onSubmit();
              }}
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ClientInfoForm;
